import { erc20Abi, type Address, type PublicClient } from "viem"
import type { Config } from "wagmi"

import { equalensMultiBalance } from "./equalens/equalens-multi-balance"

export type OnChainServiceAddresses = {
  equalensMultiBalance: Address
}

/**
 * OnChainService - Pure blockchain data service
 *
 * This service provides a clean interface for TokenRepository to fetch
 * blockchain data without knowledge of the underlying Web3 implementation.
 *
 * Focus: Pure data fetching from blockchain - no UI logic, no complex flows
 */
export class OnChainService {
  constructor(
    private readonly config: Config,
    private readonly getPublicClient: () => PublicClient,
    private readonly addresses: OnChainServiceAddresses,
  ) {
    console.log("🔗 OnChainService initialized")
  }

  /**
   * Get token balances for a user address
   * @param userAddress - The wallet address to check balances for
   * @param tokenAddresses - Array of token contract addresses
   * @returns Map of token address to balance (in wei/bigint)
   */
  async getTokenBalances(userAddress: Address, tokenAddresses: Address[]): Promise<Map<Address, bigint>> {
    // console.log(`🔍 OnChainService: Getting balances for ${tokenAddresses.length} tokens`)

    try {
      const result = (await equalensMultiBalance({
        config: this.config,
        equalensMultiBalance: this.addresses.equalensMultiBalance,
        account: userAddress,
        addresses: tokenAddresses,
      })) as Array<{ bals: Array<bigint>; token: Address; tota: bigint }>

      // console.log("result", result)
      // console.log("tokenAddresses", tokenAddresses)

      const balances = new Map<Address, bigint>()
      result.forEach((item) => {
        balances.set(item.token.toLowerCase() as Address, item.bals[0] ?? 0n)
      })

      // console.log("balances", balances)

      return balances
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug("OnChainService: token balance lens failed; falling back to ERC-20 balanceOf calls.")
      }
      return this.getTokenBalancesFallback(userAddress, tokenAddresses)
    }
  }

  private async getTokenBalancesFallback(
    userAddress: Address,
    tokenAddresses: Address[],
  ): Promise<Map<Address, bigint>> {
    try {
      const publicClient = this.getPublicClient()
      const result = await publicClient.multicall({
        allowFailure: true,
        contracts: tokenAddresses.map((address) => ({
          address,
          abi: erc20Abi,
          functionName: "balanceOf",
          args: [userAddress],
        })),
      })

      const balances = new Map<Address, bigint>()
      result.forEach((item, index) => {
        if (item.status === "success") {
          balances.set(tokenAddresses[index]!.toLowerCase() as Address, item.result)
        }
      })

      return balances
    } catch (fallbackError) {
      if (import.meta.env.DEV) {
        console.debug(
          "OnChainService: token balance fallback failed; using empty balances.",
          fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
        )
      }
      return new Map<Address, bigint>()
    }
  }

  /**
   * Get on-chain token prices
   * @param tokenAddresses - Array of token contract addresses
   * @returns Map of token address to price in USD
   */
  async getTokenPrices(tokenAddresses: Address[]): Promise<Map<Address, number>> {
    // console.log(`💰 OnChainService: Getting prices for ${tokenAddresses.length} tokens`)

    // TODO: Implement using DEX price feeds, oracles, or existing lens functions
    // For now, return zero prices
    const prices = new Map<Address, number>()

    // Stub: Add some fake data to test the flow
    tokenAddresses.forEach((address) => {
      prices.set(address, 0)
    })

    return prices
  }

  /**
   * Get Debank prices (third-party price feed)
   * @param tokenAddresses - Array of token contract addresses
   * @returns Map of token address to Debank price in USD
   */
  async getDebankPrices(tokenAddresses: Address[]): Promise<Map<Address, number>> {
    // console.log(`🏦 OnChainService: Getting Debank prices for ${tokenAddresses.length} tokens`)

    // TODO: Implement Debank API integration
    // For now, return zero prices
    const prices = new Map<Address, number>()

    tokenAddresses.forEach((address) => {
      prices.set(address, 0)
    })

    return prices
  }

  /**
   * Get native token (ETH) balance for a user address
   * @param userAddress - The wallet address to check balance for
   * @returns Balance in wei (bigint)
   */
  async getNativeBalance(userAddress: Address): Promise<bigint> {
    try {
      const publicClient = this.getPublicClient()
      return await publicClient.getBalance({ address: userAddress })
    } catch (error) {
      console.error("OnChainService: Error getting native balance", error)
      throw error
    }
  }

  /**
   * Check if service is ready to make blockchain calls
   */
  get isReady(): boolean {
    try {
      return !!(this.config && this.getPublicClient())
    } catch {
      return false
    }
  }

  /**
   * Get current chain ID from the service
   */
  get chainId(): number | undefined {
    return this.config?.state?.chainId
  }
}
